import React, { useEffect, useState } from "react";

import { getIn, useFormikContext } from "formik";
import * as Yup from "yup";

import FinanceAndAccounting from "components/Business/BusinessForm/FinanceAndAccounting";
import { GeneralCollapse } from "components/Business/BusinessForm/GeneralCollapse";
import BusinessRelationshipsTable from "components/Business/BusinessForm/Relationships/BusinessRelationshipsTable";
import { withNamespace } from "components/Form/FormikControls";

import { BusinessModalSection } from "constants/navigation";
import {
  DeploymentPermissions,
  SaleyardPermissions,
} from "constants/permissions";

import {
  useCanLinkExternalIntegrationPermission,
  useHasDeploymentPermission,
  useHasSaleyardOrDeploymentPermission,
} from "hooks";

import BusinessAddressTable from "./BusinessAddressTable";
import {
  BuyerWaysHeader,
  BuyerWaysTable,
  BuyerWayValidationSchema,
} from "./BuyerWays/BuyerWaysTable";
import DeploymentBusinessTable from "./DeploymentBusinessTable";
import EmailRecipients from "./EmailRecipients/EmailRecipients";
import IntegrationsTable from "./Integrations/IntegrationsTable";
import MergedBusinessesComponent from "./MergedBusinesses/MergedBusinessesComponent";
import { PropertyValidationSchema } from "./Properties/DeploymentPropertyForm";
import {
  PropertiesHeader,
  PropertiesTable,
} from "./Properties/PropertiesTable";
import { BusinessFormValues } from "./types";

interface DeploymentBusinessFormProps {
  defaultTab?: string;
  setIsSubFormActive: (isActive: boolean) => void;
  business: Business | null;
  namespace?: string;
  defaultAddressSearch?: string;
}

const BuyerWayTableValidationSchema = Yup.object().shape({
  editingValue: BuyerWayValidationSchema.nullable(),
});

const PropertiesTableValidationSchema = Yup.object().shape({
  editingValue: PropertyValidationSchema.nullable(),
});

export const DeploymentBusinessForm = (
  props: DeploymentBusinessFormProps,
): React.JSX.Element => {
  const {
    defaultTab,
    setIsSubFormActive,
    business,
    namespace: ns = "",
    defaultAddressSearch,
  } = props;
  const [isEditingProperty, setIsEditingProperty] = useState<boolean>(false);
  const [isEditingBuyerWay, setIsEditingBuyerWay] = useState<boolean>(false);
  const [isEditingEmailRecipient, setIsEditingEmailRecipient] =
    useState<boolean>(false);
  const [section, setSection] = useState<string | null>(
    defaultTab || BusinessModalSection.GENERAL,
  );

  const formikProps = useFormikContext<BusinessFormValues>();

  const {
    abn: abnValue,
    id: businessIdValue,
    abrLastChecked: abrLastCheckedValue,
  } = formikProps.values;

  useEffect(() => {
    setIsSubFormActive(
      Boolean(
        isEditingProperty || isEditingBuyerWay || isEditingEmailRecipient,
      ),
    );
  }, [
    isEditingProperty,
    isEditingBuyerWay,
    isEditingEmailRecipient,
    setIsSubFormActive,
  ]);

  const cancelEditEmailRecipient = (): void => {
    setIsEditingEmailRecipient(false);
  };

  const editProperty = (): void => {
    setIsEditingProperty(true);
  };

  const editEmailRecipient = (): void => {
    setIsEditingEmailRecipient(true);
  };

  const onSetPrimaryContactEmail = (emailAddress: string | null): void => {
    const { setFieldValue, setFieldTouched } = formikProps;
    const field = "primaryContactEmail";
    setFieldValue(field, emailAddress);
    setFieldTouched(field);
  };

  const toggleSection = (newSection: string | null): void => {
    if (newSection === section) {
      setSection(null);
    } else {
      setSection(newSection);
    }
  };

  const toggleAddress = (): void => {
    toggleSection(BusinessModalSection.ADDRESS);
  };

  const toggleGeneral = (): void => {
    toggleSection(BusinessModalSection.GENERAL);
  };

  const toggleProperties = (): void => {
    toggleSection(BusinessModalSection.PROPERTIES);
  };

  const toggleIntegrations = (): void => {
    toggleSection(BusinessModalSection.INTEGRATIONS);
  };

  const toggleBuyerWays = (): void => {
    toggleSection(BusinessModalSection.BUYER_WAYS);
  };

  const toggleEmailRecipients = (): void => {
    toggleSection(BusinessModalSection.EMAIL_RECIPIENTS);
  };

  const toggleBusinessRelationships = (): void => {
    toggleSection(BusinessModalSection.BUSINESS_RELATIONSHIPS);
  };

  const toggleFinanceAndAccounting = (): void => {
    toggleSection(BusinessModalSection.FINANCE_AND_ACCOUNTING);
  };

  const toggleBusinessMergeEvents = (): void => {
    toggleSection(BusinessModalSection.BUSINESS_MERGE_EVENTS);
  };

  const buyerWaysList = getIn(
    formikProps.values,
    withNamespace(ns, "buyerWays"),
  );

  const propertiesList = getIn(
    formikProps.values,
    withNamespace(ns, "properties"),
  );

  const primaryContactEmail = getIn(
    formikProps.values,
    withNamespace(ns, "primaryContactEmail"),
  );

  const hasBusinessFinanceFeature = useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.featureBusinessFinance,
    SaleyardPermissions.featureBusinessFinance,
  );

  const canLinkExternalIntegration = useCanLinkExternalIntegrationPermission();

  const hasBusinessRelationFeature = useHasDeploymentPermission(
    DeploymentPermissions.featureBusinessRelations,
  );

  const hasfeatureOrganisationalMergeBusinessFeature =
    useHasSaleyardOrDeploymentPermission(
      DeploymentPermissions.featureOrganisationalMergeBusiness,
      SaleyardPermissions.featureOrganisationalMergeBusiness,
    );

  return (
    <>
      <GeneralCollapse
        isOpen={section === BusinessModalSection.GENERAL}
        onToggle={toggleGeneral}
        namespace={ns}
        businessId={business?.id}
      />
      <BusinessAddressTable
        isOpen={section === BusinessModalSection.ADDRESS}
        onToggle={toggleAddress}
        namespace={withNamespace(ns, "address")}
        defaultAddressSearch={defaultAddressSearch}
        businessId={business?.id}
      />
      {hasBusinessFinanceFeature && (
        <FinanceAndAccounting
          isOpen={section === BusinessModalSection.FINANCE_AND_ACCOUNTING}
          onToggle={toggleFinanceAndAccounting}
          namespace={ns}
          abn={abnValue}
          businessId={businessIdValue}
          abrLastChecked={abrLastCheckedValue}
        />
      )}
      <DeploymentBusinessTable
        masterBusinessId={business?.id}
        initialValuesList={propertiesList}
        isOpen={section === BusinessModalSection.PROPERTIES}
        namespace={withNamespace(ns, "properties")}
        onStartEditing={editProperty}
        onEndEditing={(): void => {
          setIsEditingProperty(false);
        }}
        onToggle={toggleProperties}
        headerComponent={PropertiesHeader}
        tableComponent={PropertiesTable}
        validationSchema={PropertiesTableValidationSchema}
        dataTour="propertyCollapse"
      />
      {canLinkExternalIntegration && (
        <IntegrationsTable
          isOpen={section === BusinessModalSection.INTEGRATIONS}
          onToggle={toggleIntegrations}
          businessId={business?.id}
        />
      )}
      <DeploymentBusinessTable
        masterBusinessId={business?.id}
        initialValuesList={buyerWaysList}
        isOpen={section === BusinessModalSection.BUYER_WAYS}
        namespace={withNamespace(ns, "buyerWays")}
        onStartEditing={(): void => {
          setIsEditingBuyerWay(true);
        }}
        onEndEditing={(): void => {
          setIsEditingBuyerWay(false);
        }}
        onToggle={toggleBuyerWays}
        headerComponent={BuyerWaysHeader}
        tableComponent={BuyerWaysTable}
        validationSchema={BuyerWayTableValidationSchema}
        dataTour="buyerWayCollapse"
        propertiesList={propertiesList}
      />
      <EmailRecipients
        isOpen={section === BusinessModalSection.EMAIL_RECIPIENTS}
        masterBusinessId={business?.id}
        primaryContactEmail={primaryContactEmail}
        onStartEditing={editEmailRecipient}
        onEndEditing={cancelEditEmailRecipient}
        onSetPrimaryContactEmail={onSetPrimaryContactEmail}
        onToggle={toggleEmailRecipients}
        namespace={ns}
      />
      {hasBusinessRelationFeature && (
        <BusinessRelationshipsTable
          isOpen={section === BusinessModalSection.BUSINESS_RELATIONSHIPS}
          onToggle={toggleBusinessRelationships}
          namespace={ns}
        />
      )}
      {hasfeatureOrganisationalMergeBusinessFeature && (
        <MergedBusinessesComponent
          businessId={business?.id}
          onToggle={toggleBusinessMergeEvents}
          isOpen={section === BusinessModalSection.BUSINESS_MERGE_EVENTS}
        />
      )}
    </>
  );
};
